import React from "react";
import { Icons } from "../../../design_system/components/Icons";
import { SelectionControl } from "../../../design_system/components/SelectionControls/SelectionControl.jsx";
import { SegmentedControl } from "../../../design_system/components/SegmentedControl/SegmentedControl.jsx";
import {
    COVERAGE_OPTIONS,
    TYPE_ASSIGNED_TO_ME,
    NEVER,
    coverageSummaryLabel,
} from "../../utils/AllNotificationOptions";

/**
 * NotificationTypeCard — one collapsible per-post-type notification card.
 *
 * Shared by the personal (ManageNotifications) and admin (TeamNotifications)
 * tabs. Presentational + controlled: parent owns `setting` and applies updates
 * via `onChange(nextSetting)`.
 *
 * @param {object} typeConfig  - { type, label, iconName, supportsMentionedFor, frequencyOptions }
 * @param {object} setting     - { frequency, coverage, mentioned_for_enabled, mentioned_for }
 * @param {string[]} mentionedForTags - tag list for this type (from API per-type map)
 * @param {boolean} expanded
 * @param {func} onToggle      - collapse/expand handler
 * @param {func} onChange      - receives the next setting object
 * @param {string} groupKey    - makes radio group `name` unique (e.g. member id)
 */
export const NotificationTypeCard = ({
    typeConfig,
    setting,
    mentionedForTags = [],
    expanded,
    onToggle,
    onChange,
    groupKey = "",
}) => {
    const { type, label, iconName, colorKey, supportsMentionedFor, frequencyOptions } = typeConfig;
    const frequency = setting?.frequency || "Weekly";
    const coverage = setting?.coverage || "All";
    const mfEnabled = !!setting?.mentioned_for_enabled;
    const mfTags = Array.isArray(setting?.mentioned_for) ? setting.mentioned_for : [];

    const patch = (changes) => onChange({ ...setting, ...changes });

    // Hide Coverage + mentioned_for when freq=Never (Q27) — preserve their
    // current values in state so flipping back to Daily/Weekly restores them.
    const showCoverage = frequency !== NEVER;
    // mentioned_for shows under "assigned to me" only (Q6, FE-tied). Tags are
    // retained in state when leaving that coverage (Q9) — just hidden here.
    const showMentionedFor = showCoverage && supportsMentionedFor && coverage === TYPE_ASSIGNED_TO_ME;

    const toggleMfEnabled = () => patch({ mentioned_for_enabled: !mfEnabled });

    const toggleTag = (tag) => {
        const next = mfTags.includes(tag) ? mfTags.filter((t) => t !== tag) : [...mfTags, tag];
        patch({ mentioned_for: next });
    };

    return (
        <div className={`ntf-card${expanded ? " ntf-card--expanded" : ""}`}>
            <button type="button" className="ntf-card__head" onClick={onToggle}>
                <span className={`ntf-card__icon ntf-card__icon--${colorKey}`}><Icons name={iconName} size={16} /></span>
                <span className="ntf-card__head-text">
                    <span className="ntf-card__title body-s-semibold">{label}</span>
                    <span className="ntf-card__summary">
                        <span className="ntf-card__freq-badge support-m-semibold">{frequency}</span>
                        {frequency !== NEVER && (
                            <span className="ntf-card__coverage support-m">{coverageSummaryLabel(coverage)}</span>
                        )}
                    </span>
                </span>
                <Icons name={expanded ? "chevron-up" : "chevron-down"} size={20} />
            </button>

            {expanded && (
                <div className="ntf-card__body">
                    {/* Email frequency */}
                    <div className="ntf-field">
                        <div className="ntf-field__info">
                            <div className="ntf-field__title body-s-semibold">Email frequency</div>
                            <div className="ntf-field__desc body-s-semibold">How often you want digest emails for this data type.</div>
                        </div>
                        <SegmentedControl
                            options={frequencyOptions}
                            value={frequency}
                            onChange={(value) => patch({ frequency: value })}
                            ariaLabel="Email frequency"
                        />
                    </div>

                    {showCoverage && (
                        <>
                            <div className="ntf-card__divider" />

                            {/* Coverage */}
                            <div className="ntf-field">
                                <div className="ntf-field__info">
                                    <div className="ntf-field__title body-s-semibold">Coverage</div>
                                    <div className="ntf-field__desc body-s-semibold">Choose which results to receive. You can optionally filter by mention type.</div>
                                </div>

                                <div className="ntf-coverage">
                                    {COVERAGE_OPTIONS.map((opt) => (
                                        <div key={opt.value} className="ntf-option">
                                            <SelectionControl
                                                type="radio"
                                                name={`coverage-${groupKey}-${type}`}
                                                value={opt.value}
                                                checked={coverage === opt.value}
                                                onChange={() => patch({ coverage: opt.value })}
                                            />
                                            <div className="ntf-option__text" onClick={() => patch({ coverage: opt.value })}>
                                                <div className="ntf-option__title support-m-semibold">{opt.title}</div>
                                                <div className="ntf-option__desc support-m">{opt.desc}</div>
                                            </div>
                                        </div>
                                    ))}

                                    {showMentionedFor && (
                                        <>
                                            <div className="ntf-coverage__divider">
                                                <span className="ntf-coverage__divider-line" />
                                                <span className="ntf-coverage__divider-text support-s-caps">and optionally include</span>
                                                <span className="ntf-coverage__divider-line" />
                                            </div>

                                            <div className="ntf-option">
                                                <SelectionControl type="checkbox" checked={mfEnabled} onChange={toggleMfEnabled} />
                                                <div className="ntf-option__main">
                                                    <div className="ntf-option__text" onClick={toggleMfEnabled}>
                                                        <div className="ntf-option__title support-m-semibold">Specific mentioned for types</div>
                                                        <div className="ntf-option__desc support-m">Only include results tagged with the mention categories you select below.</div>
                                                    </div>

                                                    {mfEnabled && (
                                                        <div className="ntf-mentions">
                                                            <div className="ntf-mentions__head support-m-semibold">
                                                                Select mentioned for categories ({mfTags.length} selected)
                                                            </div>
                                                            <div className="ntf-mentions__tags">
                                                                {mentionedForTags.map((tag) => {
                                                                    const active = mfTags.includes(tag);
                                                                    return (
                                                                        <button
                                                                            key={tag}
                                                                            type="button"
                                                                            className={`ntf-tag support-m-semibold${active ? " ntf-tag--active" : ""}`}
                                                                            onClick={() => toggleTag(tag)}
                                                                        >
                                                                            {tag}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationTypeCard;
